import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Waves, Sun, Users } from "lucide-react";
import { fetchResorts } from "@/lib/resortsCache";
import { SectionHeading, Reveal } from "@/components/Reveal";
import Carousel from "@/components/Carousel";

const CATEGORY_LABELS = { premium: "مميز", luxury: "فخم", budget: "اقتصادي", upper: "راقية", value: "اقتصادي" };

const ResortSlide = ({ r }) => (
  <Link to={`/maldives/${r.slug}`} data-testid={`teaser-resort-${r.slug}`}
    className="group block h-full overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
    <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
      {r.images?.[0]?.file && (
        <img src={r.images[0].file} alt={r.name} loading="lazy" decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      )}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-10">
        <h3 className="text-lg font-bold text-white" dir="ltr">{r.name}</h3>
      </div>
      {r.category && (
        <span className="absolute top-3 start-3 rounded-full bg-white/90 border border-[#C80000]/20 px-3 py-1 text-[11px] font-bold text-[#C80000]">
          {CATEGORY_LABELS[r.category] || r.category}
        </span>
      )}
    </div>
    <div className="flex items-center justify-between gap-2 p-4">
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
        {r.transfer && <span className="inline-flex items-center gap-1"><Waves className="h-3.5 w-3.5 text-[#C80000]" />{r.transfer}</span>}
        {r.adultsOnly ? <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-[#C80000]" />للبالغين فقط</span> : null}
      </div>
      <span className="shrink-0 rounded-full bg-[#C80000] px-4 py-1.5 text-[11px] font-bold text-white transition-colors group-hover:bg-[#A60000]">اكتشف المنتجع</span>
    </div>
  </Link>
);

export const MaldivesTeaser = () => {
  const [resorts, setResorts] = useState(null);

  useEffect(() => {
    fetchResorts().then((data) => setResorts(data)).catch(() => setResorts([]));
  }, []);

  if (resorts !== null && !resorts.length) return null; // فشل الجلب — أخفِ القسم كما كان
  if (resorts === null)
    return (
      // هيكل بنفس ارتفاع الشبكة أثناء الجلب — ثبات التخطيط دون تغيير التصميم النهائي
      <section className="py-24 sm:py-32" aria-hidden="true">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-12 space-y-4 animate-pulse">
            <div className="h-3 w-32 rounded-full bg-slate-100" />
            <div className="h-8 w-64 rounded-full bg-slate-100" />
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 animate-pulse">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-3xl border border-slate-200 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100" />
                <div className="p-5 space-y-2.5"><div className="h-4 w-2/3 rounded-full bg-slate-100" /><div className="h-3 w-1/2 rounded-full bg-slate-100" /></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );

  return (
    <section id="resorts" className="relative border-b border-slate-200 bg-[#F8F9FA] py-20 sm:py-24 overflow-hidden" data-testid="maldives-teaser">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="flex items-end justify-between gap-4">
            <SectionHeading caption="وجهتنا الحالية" title="منتجعات المالديف" />
            <Link to="/maldives" data-testid="teaser-browse-all"
              className="mb-2 hidden sm:inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-2.5 text-xs font-bold text-slate-600 transition-colors hover:border-[#C80000]/50 hover:text-[#C80000]">
              تصفّح جميع المنتجعات <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10">
            <Carousel items={resorts} testid="resorts-carousel" ariaLabel="معرض منتجعات المالديف"
              renderItem={(r) => <ResortSlide r={r} />} />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-8 text-center sm:hidden">
            <Link to="/maldives" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-2.5 text-xs font-bold text-slate-600">
              تصفّح جميع المنتجعات <ArrowLeft className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
